
import { logger } from "@/lib/logger";
import { WebsiteAnalysisResult, AnalysisErrorType } from "../types";
import { fetchWebsiteContent } from "../fetchContent";
import { analyzeContentWithAI } from "../aiAnalysis";
import { analyzeWithEdgeFunction } from "../edgeFunction";
import * as Sentry from "@sentry/react";
import { startAnalysisTransaction, configureAnalysisScope } from "../utils/sentryHelpers";
import { hasValidAnalysisData } from "../utils/validationUtils";

/**
 * Main function to analyze a website using AI
 * @param url The website URL to analyze
 * @returns Analysis results with business information
 */
export async function analyzeWebsiteWithAI(url: string): Promise<WebsiteAnalysisResult> {
  let transaction = null;
  try {
    transaction = startAnalysisTransaction();
    configureAnalysisScope(url);

    logger.info({
      message: "Starting website analysis",
      context: { url }
    });
    
    // First try with the edge function as it has proper error handling
    if (transaction) transaction.setName("website-analysis-edge");
    Sentry.addBreadcrumb({
      category: 'analysis',
      message: 'Attempting analysis with edge function',
      level: 'info'
    });
    
    try {
      const edgeFunctionResult = await analyzeWithEdgeFunction(url);
      
      // Log the edge function result for debugging
      logger.info({
        message: "Edge function returned result",
        context: { 
          url, 
          resultKeys: Object.keys(edgeFunctionResult),
          hasBusinessName: !!edgeFunctionResult.businessName,
          hasIndustry: !!edgeFunctionResult.industry,
          hasDescription: !!edgeFunctionResult.description,
          keywordsCount: edgeFunctionResult.keywords?.length || 0,
          colorsCount: edgeFunctionResult.colors?.length || 0
        }
      });
      
      // Validate that we have meaningful data
      if (hasValidAnalysisData(edgeFunctionResult)) {
        if (transaction) transaction.finish();
        return edgeFunctionResult;
      }
      
      // If edge function returned empty/invalid data, throw an error to try client-side analysis
      logger.warn({
        message: "Edge function returned incomplete analysis results, trying client-side analysis",
        context: { url, resultKeys: Object.keys(edgeFunctionResult) }
      });
      throw new Error("Edge function returned incomplete analysis results");
    } catch (edgeFunctionError) {
      return await handleEdgeFunctionFailure(edgeFunctionError, url, transaction);
    }
  } catch (error) {
    handleAnalysisFailure(error, url);
    // Ensure the transaction is finished in the error case
    if (transaction) transaction.finish();
    // Return a result that clearly indicates failure
    throw new Error("Failed to analyze website content");
  } finally {
    // Ensure the transaction is always finished
    if (transaction) transaction.finish();
  }
}

/**
 * Handle edge function failure by trying client-side analysis
 */
async function handleEdgeFunctionFailure(
  edgeFunctionError: unknown, 
  url: string,
  transaction?: Sentry.Transaction | null
): Promise<WebsiteAnalysisResult> {
  const errorMessage = edgeFunctionError instanceof Error ? 
    edgeFunctionError.message : String(edgeFunctionError);
  
  logger.warn({
    message: "Edge function analysis failed, attempting fallback to client-side analysis",
    error: edgeFunctionError instanceof Error ? edgeFunctionError : new Error(errorMessage),
    context: { url }
  });
  
  Sentry.addBreadcrumb({
    category: 'analysis',
    message: 'Edge function failed, falling back to client-side analysis',
    level: 'warning',
    data: {
      error: errorMessage
    }
  });
  
  // Only if edge function fails, try to fetch website content and analyze locally
  if (transaction) transaction.setName("website-analysis-client-side");
  
  const websiteContent = await fetchWebsiteContent(url);
  
  if (!websiteContent || websiteContent.length < 100) {
    logger.error({
      message: "Website content too short or empty",
      context: { 
        url,
        contentLength: websiteContent ? websiteContent.length : 0
      }
    });
    throw new Error("Website content too short or empty");
  }
  
  const clientSideResult = await analyzeContentWithAI(websiteContent, url);
  
  // Log the client-side result for debugging
  logger.info({
    message: "Client-side analysis complete",
    context: { 
      url, 
      resultKeys: Object.keys(clientSideResult),
      hasBusinessName: !!clientSideResult.businessName,
      hasIndustry: !!clientSideResult.industry,
      hasDescription: !!clientSideResult.description,
      keywordsCount: clientSideResult.keywords?.length || 0,
      colorsCount: clientSideResult.colors?.length || 0
    }
  });
  
  // Validate client-side results as well
  if (hasValidAnalysisData(clientSideResult)) {
    if (transaction) transaction.finish();
    return clientSideResult;
  }
  
  // If client-side analysis also failed, throw an error
  logger.error({
    message: "Client-side analysis returned incomplete results",
    context: { 
      url,
      resultKeys: Object.keys(clientSideResult)
    }
  });
  throw new Error("Client-side analysis returned incomplete results");
}

/**
 * Handle final analysis failure
 */
function handleAnalysisFailure(error: unknown, url: string): void {
  const finalErrorMessage = error instanceof Error ? error.message : String(error);
  
  logger.error({
    message: "Error analyzing website with AI",
    error: error instanceof Error ? error : new Error(finalErrorMessage),
    context: { 
      url,
      errorType: AnalysisErrorType.UNKNOWN_ERROR,
      fallbackFailed: true
    }
  });
  
  Sentry.captureException(error, {
    tags: {
      error_type: AnalysisErrorType.UNKNOWN_ERROR,
      fallback_failed: "true"
    },
    contexts: {
      analysis: {
        url,
        original_error: finalErrorMessage
      }
    }
  });
}
